import type { Dayjs } from "dayjs";
import type { Options as HighchartsOptions } from "highcharts";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import { renderToStaticMarkup, renderToString } from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";

import type { ICategory } from "app/common/services/categoryService.types";
import { hasAccessToPackage } from "app/common/services/solutions2Helper";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { trafficSources } from "app/Shared/utils";
import { utils } from "app/Shared/utils";
import { swSettings } from "app/common/services/swSettings";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { AllTrafficChannelsOverviewBarChartCompareModeTooltip } from "./AllTrafficChannelsOverviewBarChartCompareModeTooltip";
import { AllTrafficChannelsOverviewBarChartTooltip } from "./AllTrafficChannelsOverviewBarChartTooltip";
import { translateSourceText } from "./ChannelsOverviewBarChartConfig";
import {
    type DataFormat,
    numbersFilter,
    percentDataFormat,
    percentsFilter,
} from "./ChannelsOverviewBarChartContainerFormat";
import { ChannelsOverviewCategoryLabel } from "./components/category-label/ChannelsOverviewCategoryLabel";
import type { IData } from "./components/view-by-domain-chart/types";
import type {
    ITrafficSources,
    ITotalDeviceData,
    TrafficDataByDeviceType,
    IndustryDataByDeviceType,
    ILegendItem,
} from "./types";

const i18n = i18nFilter();
const calcTotal = (data) => {
    let total: any = 0;
    for (const [key, value] of Object.entries(data)) {
        for (const channel in value as any) {
            total += value[channel];
        }
    }
    return total;
};

export const getSeries = (data: TrafficDataByDeviceType, dataFormat, fromDate) => {
    if (isEmpty(data) || !data.Total) {
        return undefined;
    }

    const keys = getCategoriesKey(data.Total);
    const total = Object.values(data.Total).reduce<number>((sum, domainSources) => {
        return sum + Object.values(domainSources).reduce<number>((s, v) => s + v, 0);
    }, 0);

    const seriesData = keys.map((key) => {
        const value = Object.values(data.Total).reduce<number>((sum, domainSources) => {
            return sum + (domainSources[key] ?? 0);
        }, 0);

        return dataFormat === percentDataFormat
            ? total > 0 ? (value / total) * 100 : 0
            : value;
    });

    return [
        {
            id: "Total",
            name: "Total",
            data: seriesData,
            color: colorsPalettes.navigation.ACTIVE_BLUE,
            visible: true,
            key: "Total",
            borderColor: colorsPalettes.navigation.ACTIVE_BLUE,
        },
    ];
};

const getPercentage = (pointValue = 0, totalVisits, dataFormat) => {
    return 100 * (pointValue / totalVisits);
};

const getCategoriesKey = (data) => {
    const chartItemsInfo = utils.volumesAndSharesSplited;
    const keys = [];
    const categoriesKeys = Array.from(
        Object.values<any>(data).reduce((acc, current) => {
            Object.keys(current).forEach((k) => acc.add(k));
            return acc;
        }, new Set([])),
    );
    const categories = categoriesKeys.map((key) => ({ key }));
    sortBy(categories, chartItemsInfo.sort).map((cat) => {
        const key = Object.values(cat);
        keys.push(key[0]);
    });
    return keys;
};
const getCategories = (data, fromDate, isCompare) => {
    const chartItemsInfo = utils.volumesAndSharesSplited;
    const categoriesKeys = Array.from(
        Object.values<any>(data.Total).reduce((acc, current) => {
            Object.keys(current).forEach((k) => acc.add(k));
            return acc;
        }, new Set([])),
    );
    const categories = categoriesKeys.map((key) => ({ key }));
    const obj = sortBy(categories, chartItemsInfo.sort).map((cat: { key: string }) => {
        const key = cat.key;
        const item = chartItemsInfo.order[key];
        return {
            key,
            item,
            title: translateSourceText(key),
        };
    });
    return obj;
};

type TrafficSourceDefinition = (typeof trafficSources)[keyof typeof trafficSources];

interface ICategoryLabelFormatterProps {
    key: string;
    trafficSourceDefinition?: TrafficSourceDefinition;
}

export const formatCategoryLabel = ({ key, trafficSourceDefinition }: ICategoryLabelFormatterProps) => {
    const label = translateSourceText(key);
    const tooltip = i18n((trafficSourceDefinition && trafficSourceDefinition.title) || key);
    let href: string | null = null;

    if (trafficSourceDefinition && "state" in trafficSourceDefinition) {
        const itemState =
            trafficSourceDefinition.title === "utils.paidSearch"
                ? "digitalsuite_website_search_overview"
                : trafficSourceDefinition.state;

        href = proNavigator.href(itemState, proNavigator.getParams());

        if ("queryParams" in trafficSourceDefinition && trafficSourceDefinition.queryParams !== "") {
            href = `${href}&${trafficSourceDefinition.queryParams}`;
        }
        const isSimilarWebUser = swSettings.user.isSimilarWebUser;
        const hasLimitedSIAccess =
            hasAccessToPackage("salesIntelligence") && !salesSettingsHelper.isMarkettingChannelsAdonEnabled;

        if (!isSimilarWebUser && hasLimitedSIAccess) {
            href = null;
        }
    }

    return renderToStaticMarkup(
        <ChannelsOverviewCategoryLabel
            categoryKey={key}
            href={href}
            label={label}
            tooltip={tooltip}
        />,
    );
};

export const getChartConfigSingleMode = ({ data, dataFormat, isCompare, fromDate }) => {
    const config: any = {
        colors: Object.values(CHART_COLORS.trafficSourcesColorsBySourceMMX),
        chart: {
            height: 220,
            type: "column",
            animation: false,
            spacingTop: 20,
        },
        legend: {
            enabled: false,
        },
        xAxis: {
            categories: getCategories(data, fromDate, false),
            lineColor: colorsPalettes.carbon[100],
            gridLineColor: colorsPalettes.carbon[50],
            crosshair: true,
            tickWidth: 0,
            labels: {
                align: "center",
                rotation: 0,
                useHTML: true,
                style: {
                    textAlign: "center",
                    fontSize: "14px",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[400],
                    fontWeight: 400,
                },
                formatter: function () {
                    const { key, item } = this.value;
                    return formatCategoryLabel({ key, trafficSourceDefinition: item });
                },
            },
        },
        yAxis: {
            gridLineColor: colorsPalettes.carbon[25],
            min: dataFormat === percentDataFormat ? 0 : null,
            max: null,
            startOnTick: false,
            endOnTick: true,
            title: null,
            opposite: false,
            showLastLabel: true,
            color: colorsPalettes.carbon[400],
            stackLabels: {
                enabled: true,
                useHTML: true,
                crop: false,
                formatter() {
                    return this.total === 0
                        ? "N/A"
                        : dataFormat === percentDataFormat
                          ? percentsFilter(this.total)
                          : numbersFilter(this.total);
                },
                style: {
                    fontSize: "14px",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[300],
                    fontWeight: 400,
                    spacingTop: 20,
                },
                overflow: "none",
            },
            labels: {
                align: "right",
                enabled: true,
                useHTML: true,
                x: -10,
                formatter() {
                    return dataFormat === percentDataFormat
                        ? this.value + percentDataFormat
                        : abbrNumberFilter()(this.value);
                },
                style: {
                    fontSize: "12px",
                    textTransform: "uppercase",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[400],
                },
            },
        },

        tooltip: {
            ...tooltipContainerConfig().tooltip,
            enabled: !isCompare,
            shared: true,
            outside: true,
            followPointer: true,
            useHTML: true,
            formatter: function () {
                return AllTrafficChannelsOverviewBarChartTooltip({
                    points: this.points,
                    dataFormat,
                    isCompare,
                    total: calcTotal(data?.Total),
                });
            },
        },
        plotOptions: {
            column: {
                stacking: "normal",
                borderWidth: 0,
                pointWidth: isCompare ? null : 50,
                minPointLength: 3,
                grouping: true,
                maxPointWidth: 40,
                pointPadding: 0.15,
                pointRange: 1,
            },
        },
    };
    return config;
};

type IGetChartConfigCompareMode = {
    data: TrafficDataByDeviceType;
    legends: ILegendItem[];
    industryData?: IndustryDataByDeviceType;
    category?: ICategory;
    dataFormat: DataFormat;
    fromDate: Dayjs;
};

export const getChartConfigCompareMode = ({
    data,
    legends,
    industryData,
    category,
    dataFormat,
    fromDate,
}: IGetChartConfigCompareMode) => {
    const config: HighchartsOptions = {
        colors: Object.values(CHART_COLORS.trafficSourcesColorsBySourceMMX),
        chart: {
            height: 297,
            type: "column",
            animation: false,
            spacingTop: 20,
        },
        xAxis: {
            categories: getCategories(data, fromDate, true).map(({ key }) => key),
            lineColor: colorsPalettes.carbon[100],
            gridLineColor: colorsPalettes.carbon[50],
            crosshair: true,
            tickWidth: 0,
            labels: {
                align: "center",
                rotation: 0,
                useHTML: true,
                style: {
                    fontSize: "14px",
                    textAlign: "center",
                    fontFamily: '"Roboto", sans-serif',
                },
                formatter() {
                    const key = String(this.value);
                    const trafficSourceDefinition: TrafficSourceDefinition | undefined =
                        utils.volumesAndSharesSplited.order[key];

                    return formatCategoryLabel({ key, trafficSourceDefinition });
                },
            },
        },
        yAxis: {
            gridLineColor: colorsPalettes.carbon[25],
            min: dataFormat === percentDataFormat ? 0 : null,
            max: null,
            startOnTick: false,
            endOnTick: true,
            title: null,
            opposite: false,
            showLastLabel: true,
            color: colorsPalettes.carbon[400],
            labels: {
                align: "right",
                enabled: true,
                useHTML: true,
                x: -10,
                formatter() {
                    return dataFormat === percentDataFormat
                        ? this.value + percentDataFormat
                        : abbrNumberFilter()(this.value);
                },
                style: {
                    fontSize: "12px",
                    textTransform: "uppercase",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[400],
                },
            },
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                pointWidth: null,
                minPointLength: 3,
                grouping: true,
                maxPointWidth: 40,
                pointPadding: 0.15,
                pointRange: 1,
                dataLabels: {
                    enabled: false,
                    crop: false,
                    formatter() {
                        return this.y
                            ? dataFormat === percentDataFormat
                                ? percentsFilter(this.y)
                                : numbersFilter(this.y)
                            : "N/A";
                    },
                    color: colorsPalettes.carbon[200],
                    useHTML: true,
                    style: {
                        fontFamily: '"Roboto", sans-serif',
                        fontSize: "14px",
                        fontWeight: "400",
                    },
                    overflow: "justify",
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        tooltip: {
            ...tooltipContainerConfig().tooltip,
            enabled: true,
            shared: true,
            outside: true,
            followPointer: true,
            useHTML: true,
            formatter() {
                return renderToString(
                    <AllTrafficChannelsOverviewBarChartCompareModeTooltip
                        trafficSource={String(this.key)}
                        trafficData={data}
                        industryData={industryData}
                        legends={legends}
                        category={category}
                        points={this.points}
                        dataFormat={dataFormat}
                    />,
                );
            },
        },
    };
    return config;
};

export const transformDomainTrafficData = (totalData: ITotalDeviceData): IData => {
    const result: IData = {};

    for (const domain in totalData) {
        if (Object.prototype.hasOwnProperty.call(totalData, domain)) {
            const trafficSources: ITrafficSources = { ...totalData[domain] };
            const totalVisits = Object.values(trafficSources).reduce((sum, value) => sum + value, 0);

            result[domain] = {
                totalVisits,
                trafficSources,
            };
        }
    }

    return result;
};

export const hasAnyRealTraffic = (
    total?: Record<string, Record<string, number | null | undefined>>
): boolean => {
    if (!total) return false;

    return Object.values(total).some((domainData) =>
        Object.values(domainData ?? {}).some(
            (val) => typeof val === "number" && val > 0
        )
    );
};